import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { btnPrimary } from '@/components/rm'
import { BrandMark } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'
import { landingPath, useSaveProfile } from '@/lib/account'

// Spec 002. The one question a new account must answer: the name a trade person will see.
export const Route = createFileRoute('/welcome')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw redirect({ to: '/login' })
  },
  component: WelcomePage,
})

function WelcomePage() {
  const { t } = useT()
  const navigate = useNavigate()
  const save = useSaveProfile()
  const [name, setName] = useState('')
  const [error, setError] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = name.trim().replace(/\s+/g, ' ')
    if (!n) return setError(t('welcome.required'))
    setError('')
    await save.mutateAsync({ display_name: n.slice(0, 80) })
    navigate({ to: await landingPath() })
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-canvas-subtle px-4 py-10 sm:items-center">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center"><LanguageMenu /></div>
          <BrandMark className="mx-auto mb-3 h-11 w-11" />
          <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('welcome.title')}</h1>
          <p className="mt-1 text-[13.5px] text-token-secondary">{t('welcome.subtitle')}</p>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
          <div className="space-y-1.5">
            <label htmlFor="name" className="text-[13px] font-semibold text-token-secondary">{t('welcome.label')}</label>
            <input id="name" autoFocus autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              className="h-11 w-full rounded-[10px] border border-token-subtle px-3 text-[15px] text-token-primary outline-none focus:border-token-strong focus:ring-2 focus:ring-brand/10"
              placeholder={t('welcome.placeholder')} />
          </div>
          {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
          <button type="submit" disabled={save.isPending} className={`${btnPrimary} h-11 w-full justify-center`}>{t('welcome.continue')}</button>
        </form>
      </div>
    </div>
  )
}
